// =============================================================================
// Origin UI — src/pages/creditcards/MyCardPage.tsx
// Cardholder's transaction review page. Shows transactions for the current
// period with inline editable commentary, personal/disputed flags,
// card selector, delegate on-behalf, submit with validation, and receipt upload.
// CC-12.1 through CC-12.6
// =============================================================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import {
  getPeriods, getMyCardsForPeriod, getMyTransactions, updateCommentary,
  submitReview, recallReview, uploadReceipt, getReceipts, deleteReceipt,
  type StatementPeriodListItem, type MyCardForPeriod, type CardTransactionListItem,
  type CardReceipt
} from '@/services/creditCardApi';
import { getMyDelegations, type DelegationTarget } from '@/services/creditCardApi';
import { api } from '@/services/apiService';
import styles from './creditcards.module.css';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export function MyCardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allPeriods, setAllPeriods] = useState<StatementPeriodListItem[]>([]);
  const [activePeriodId, setActivePeriodId] = useState<string>('');
  const [cards, setCards] = useState<MyCardForPeriod[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [transactions, setTransactions] = useState<CardTransactionListItem[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Delegate support

  const [savingTxId, setSavingTxId] = useState<string | null>(null);

  // Receipt state
  const [receiptCounts, setReceiptCounts] = useState<Record<string, number>>({});
  const [expandedReceiptTxId, setExpandedReceiptTxId] = useState<string | null>(null);
  const [receiptList, setReceiptList] = useState<CardReceipt[]>([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState<CardReceipt | null>(null);

  // Delegate on-behalf
  const [delegations, setDelegations] = useState<DelegationTarget[]>([]);
  const [onBehalfOf, setOnBehalfOf] = useState<string>('');

  // Sort
  const [txSortKey, setTxSortKey] = useState<'transactionDate' | 'merchantName' | 'merchantCategory' | 'amount'>('transactionDate');
  const [txSortDir, setTxSortDir] = useState<'asc' | 'desc'>('asc');
  function toggleTxSort(key: typeof txSortKey) {
    if (txSortKey === key) setTxSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTxSortKey(key); setTxSortDir('asc'); }
  }
  function txSortIcon(key: typeof txSortKey) { return txSortKey !== key ? ' ⇕' : txSortDir === 'asc' ? ' ↑' : ' ↓'; }
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      let cmp = 0;
      if (txSortKey === 'amount') cmp = a.amount - b.amount;
      else cmp = ((a as any)[txSortKey] || '').localeCompare((b as any)[txSortKey] || '');
      return txSortDir === 'asc' ? cmp : -cmp;
    });
  }, [transactions, txSortKey, txSortDir]);

  useEffect(() => { loadInitial(); }, []);

  async function loadInitial() {
    try {
      setLoading(true);
      const [p, myDelegations] = await Promise.all([
        getPeriods(),
        getMyDelegations().catch((): DelegationTarget[] => [])
      ]);
      setDelegations(myDelegations);
      setAllPeriods(p);

      const open = p.find((pp: StatementPeriodListItem) => pp.status === 'Open' || pp.status === 'UnderReview');
      const selected = open || p[0];
      if (selected) setActivePeriodId(selected.statementPeriodId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (activePeriodId) loadCards(); }, [activePeriodId, onBehalfOf]);

  async function loadCards() {
    if (!activePeriodId) return;
    try {
      const c = await getMyCardsForPeriod(activePeriodId, onBehalfOf || undefined);
      setCards(c);
      if (c.length > 0) {
        setSelectedCardId(c[0].corporateCardId);
      } else {
        setSelectedCardId('');
        setTransactions([]);
      }
    } catch (e: any) { setError(e.message); }
  }

  useEffect(() => { if (activePeriodId && selectedCardId) loadTransactions(); }, [selectedCardId]);

  async function loadTransactions() {
    if (!activePeriodId || !selectedCardId) return;
    try {
      setLoadingTx(true);
      setSubmitError('');
      setSubmitSuccess('');
      setExpandedReceiptTxId(null);
      const txns = await getMyTransactions(activePeriodId, selectedCardId, onBehalfOf || undefined);
      setTransactions(txns);

      // Load receipt counts for all transactions
      const counts: Record<string, number> = {};
      await Promise.all(txns.map(async (tx) => {
        try {
          const receipts = await getReceipts(tx.cardTransactionId);
          if (receipts.length > 0) counts[tx.cardTransactionId] = receipts.length;
        } catch { /* ignore */ }
      }));
      setReceiptCounts(counts);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingTx(false); }
  }

  const handleCommentaryBlur = useCallback(async (txId: string, commentary: string, personal: boolean, disputed: boolean) => {
    try {
      setSavingTxId(txId);
      await updateCommentary(txId, { commentary, isFlaggedPersonal: personal, isFlaggedDisputed: disputed });
      setTransactions(prev => prev.map(t => t.cardTransactionId === txId ? { ...t, commentary, isFlaggedPersonal: personal, isFlaggedDisputed: disputed } : t));
    } catch (e: any) { setError(`Failed to save: ${e.message}`); }
    finally { setSavingTxId(null); }
  }, []);

  const handleFlagToggle = useCallback(async (txId: string, field: 'personal' | 'disputed', value: boolean) => {
    const tx = transactions.find(t => t.cardTransactionId === txId);
    if (!tx) return;
    const personal = field === 'personal' ? value : tx.isFlaggedPersonal;
    const disputed = field === 'disputed' ? value : tx.isFlaggedDisputed;
    try {
      setSavingTxId(txId);
      await updateCommentary(txId, { commentary: tx.commentary || '', isFlaggedPersonal: personal, isFlaggedDisputed: disputed });
      setTransactions(prev => prev.map(t => t.cardTransactionId === txId ? { ...t, isFlaggedPersonal: personal, isFlaggedDisputed: disputed } : t));
    } catch (e: any) { setError(`Failed to save: ${e.message}`); }
    finally { setSavingTxId(null); }
  }, [transactions]);

  // ─── Receipt handlers ───

  async function handleReceiptToggle(txId: string) {
    if (expandedReceiptTxId === txId) {
      setExpandedReceiptTxId(null);
      setReceiptList([]);
      return;
    }
    setExpandedReceiptTxId(txId);
    setLoadingReceipts(true);
    try {
      const list = await getReceipts(txId);
      setReceiptList(list);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingReceipts(false); }
  }

  async function handleReceiptUpload(txId: string, file: File) {
    try {
      setUploadingReceipt(true);
      const receipt = await uploadReceipt(txId, file);
      setReceiptList(prev => [...prev, receipt]);
      setReceiptCounts(prev => ({ ...prev, [txId]: (prev[txId] || 0) + 1 }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingReceipt(false);
    }
  }

  async function handleReceiptDelete() {
    if (!receiptToDelete) return;
    const txId = receiptToDelete.cardTransactionId;
    try {
      await deleteReceipt(receiptToDelete.cardTransactionReceiptId);
      setReceiptList(prev => prev.filter(r => r.cardTransactionReceiptId !== receiptToDelete.cardTransactionReceiptId));
      setReceiptCounts(prev => {
        const count = (prev[txId] || 1) - 1;
        if (count <= 0) { const next = { ...prev }; delete next[txId]; return next; }
        return { ...prev, [txId]: count };
      });
    } catch (e: any) { setError(e.message); }
    finally { setReceiptToDelete(null); }
  }

  async function handleRecall() {
    if (!currentCard?.reviewId) return;
    try {
      setRecalling(true);
      await recallReview(currentCard.reviewId);
      setSubmitSuccess('Review recalled. You can now edit your transactions.');
      setTimeout(() => setSubmitSuccess(''), 5000);
      await loadCards();
      await loadTransactions();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setSubmitError(body?.message || body?.error || e.message);
    } finally { setRecalling(false); }
  }

  async function handleSubmit() {
    const card = cards.find(c => c.corporateCardId === selectedCardId);
    if (!card?.reviewId) return;
    // Force-blur any active commentary input to trigger pending saves
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    await new Promise(r => setTimeout(r, 600));
    // Re-fetch fresh from server to check actual saved state
    const freshTxns = await getMyTransactions(activePeriodId!, selectedCardId!, onBehalfOf || undefined);
    setTransactions(freshTxns);
    const missing = freshTxns.filter(t => !t.commentary || t.commentary.trim() === '');
    if (missing.length > 0) { setSubmitError(missing.length + ' transaction(s) still need commentary.'); return; }
    try {
      setSubmitting(true);
      setSubmitError('');
      await submitReview(card.reviewId);
      setSubmitSuccess('Review submitted successfully.');
      await loadCards();
      if (activePeriodId && selectedCardId) await loadTransactions();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setSubmitError(body?.error || body?.message || e.message);
    } finally { setSubmitting(false); }
  }

  if (loading) return <LoadingSpinner />;

  const activePeriod = allPeriods.find(p => p.statementPeriodId === activePeriodId);
  const currentCard = cards.find(c => c.corporateCardId === selectedCardId);
  const reviewStatus = currentCard?.reviewStatus || 'PendingReview';
  const isReadOnly = reviewStatus === 'Approved';
  const reviewed = transactions.filter(t => t.commentary && t.commentary.trim() !== '').length;
  const unreviewed = transactions.length - reviewed;
  const totalSpend = transactions.reduce((sum, t) => sum + t.amount, 0);
  const flagged = transactions.filter(t => t.isFlaggedPersonal || t.isFlaggedDisputed).length;
  const periodIdx = allPeriods.findIndex(p => p.statementPeriodId === activePeriodId);
  const canGoBack = periodIdx < allPeriods.length - 1;
  const canGoForward = periodIdx > 0;

  return (
    <div>
      <PageHeader title="My Card" subtitle="Corporate credit card transaction review" />
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}



      {/* Delegate bar */}
      {delegations.length > 0 && (
        <div style={{ marginBottom: 12, padding: '10px 16px', background: 'var(--turquoise-light)', borderRadius: 8, border: '1px solid var(--turquoise)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--turquoise-dark)' }}>Viewing as:</label>
          <select style={{ fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--turquoise)', background: 'white', color: 'var(--navy)' }} value={onBehalfOf} onChange={e => setOnBehalfOf(e.target.value)}>
            <option value="">Myself</option>
            {delegations.map(d => <option key={d.employeeId} value={d.employeeId}>{d.fullName} ({d.employeeNumber})</option>)}
          </select>
        </div>
      )}

      {/* Period navigator */}
      <div className={styles.periodNav}>
        <button className={styles.periodNavBtn} disabled={!canGoBack} onClick={() => setActivePeriodId(allPeriods[periodIdx + 1].statementPeriodId)}>← Previous</button>
        <div className={styles.periodNavCurrent}>
          <span className={styles.periodNavLabel}>{activePeriod?.periodName || 'No period'}</span>
          <span className={styles.periodNavStatus} style={{ color: activePeriod?.status === 'Closed' ? 'var(--green)' : activePeriod?.status === 'UnderReview' ? 'var(--turquoise)' : 'var(--orange)' }}>
            {activePeriod?.status || ''}
          </span>
        </div>
        <button className={styles.periodNavBtn} disabled={!canGoForward} onClick={() => setActivePeriodId(allPeriods[periodIdx - 1].statementPeriodId)}>Next →</button>
      </div>

      {!activePeriod || cards.length === 0 ? (
        <Card><p style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 15 }}>
          {!activePeriod ? 'No statement periods available.' : 'No transactions found for this period.'}
        </p></Card>
      ) : (
        <>
          {/* Card header */}
          {currentCard && (
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <div className={styles.cardChip}>💳</div>
                <div>
                  <div className={styles.cardNumber}>•••• •••• •••• {currentCard.lastFourDigits}</div>
                  <div className={styles.cardMeta}>{currentCard.cardIssuer} · {currentCard.cardCurrency}</div>
                </div>
              </div>
              {cards.length > 1 && (
                <select className={styles.cardSelectDropdown} value={selectedCardId} onChange={e => setSelectedCardId(e.target.value)}>
                  {cards.map(c => <option key={c.corporateCardId} value={c.corporateCardId}>•••• {c.lastFourDigits} ({c.cardIssuer})</option>)}
                </select>
              )}
              <div className={styles.cardHeaderRight}>
                <span className={styles.reviewStatusBadge} style={{
                  background: reviewStatus === 'Approved' ? 'var(--green-light)' : reviewStatus === 'Submitted' ? 'var(--turquoise-light)' : 'var(--orange-light)',
                  color: reviewStatus === 'Approved' ? 'var(--green)' : reviewStatus === 'Submitted' ? 'var(--turquoise-dark)' : 'var(--orange)',
                }}>
                  {reviewStatus === 'PendingReview' ? 'Pending Review' : reviewStatus}
                </span>
              </div>
            </div>
          )}

          {/* Status banners */}
          {reviewStatus === 'Submitted' && (
            <div className={styles.statusBanner} style={{ background: 'var(--turquoise-light)', color: 'var(--turquoise-dark)', border: '1px solid var(--turquoise)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Your transactions have been submitted and sent to your approver.</span>
              <button onClick={handleRecall} disabled={recalling} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid var(--orange)', background: 'white', color: 'var(--orange)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{recalling ? 'Recalling...' : 'Recall & Edit'}</button>
            </div>
          )}
          {reviewStatus === 'Approved' && (
            <div className={styles.statusBanner} style={{ background: 'var(--green-light)', color: 'var(--green)', border: '1px solid var(--green)' }}>
              ✅ <strong>Approved</strong> — No further action needed.
            </div>
          )}

          {/* Summary stats */}
          <div className={styles.statCards}>
            <div className={styles.statCard}><div className={styles.statCardValue}>{transactions.length}</div><div className={styles.statCardLabel}>Transactions</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: 'var(--green)' }}>{reviewed}</div><div className={styles.statCardLabel}>Reviewed</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: unreviewed > 0 ? 'var(--orange)' : 'var(--green)' }}>{unreviewed}</div><div className={styles.statCardLabel}>Unreviewed</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue}>{currentCard?.cardCurrency || 'AED'} {totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div><div className={styles.statCardLabel}>Total Spend</div></div>
            {flagged > 0 && <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: 'var(--red)' }}>{flagged}</div><div className={styles.statCardLabel}>Flagged</div></div>}
          </div>

          {/* Progress bar */}
          {!isReadOnly && transactions.length > 0 && (
            <div className={styles.progressWrap}>
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${(reviewed / transactions.length) * 100}%` }} />
              </div>
              <div className={styles.progressLabel}>{reviewed}/{transactions.length} reviewed</div>
            </div>
          )}

          {/* Transaction table */}
          {loadingTx ? <LoadingSpinner /> : (
            <Card padding={false}>
              <div className={styles.txTableWrap}>
                <table className={styles.txTable}>
                  <thead>
                    <tr>
                      <th style={{ width: 90, cursor: 'pointer' }} onClick={() => toggleTxSort('transactionDate')}>Date{txSortIcon('transactionDate')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleTxSort('merchantName')}>Merchant{txSortIcon('merchantName')}</th>
                      <th style={{ width: 160, cursor: 'pointer' }} onClick={() => toggleTxSort('merchantCategory')}>Category{txSortIcon('merchantCategory')}</th>
                      <th style={{ width: 120, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleTxSort('amount')}>Amount{txSortIcon('amount')}</th>
                      <th style={{ minWidth: 200 }}>Commentary</th>
                      <th style={{ width: 55, textAlign: 'center' }}>Personal</th>
                      <th style={{ width: 55, textAlign: 'center' }}>Disputed</th>
                      <th style={{ width: 55, textAlign: 'center' }}>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTransactions.map(tx => (
                      <TransactionRow
                        key={tx.cardTransactionId}
                        tx={tx}
                        readOnly={isReadOnly}
                        saving={savingTxId === tx.cardTransactionId}
                        receiptCount={receiptCounts[tx.cardTransactionId] || 0}
                        isReceiptExpanded={expandedReceiptTxId === tx.cardTransactionId}
                        receiptList={expandedReceiptTxId === tx.cardTransactionId ? receiptList : []}
                        loadingReceipts={expandedReceiptTxId === tx.cardTransactionId && loadingReceipts}
                        uploadingReceipt={expandedReceiptTxId === tx.cardTransactionId && uploadingReceipt}
                        onBlur={handleCommentaryBlur}
                        onFlagToggle={handleFlagToggle}
                        onReceiptToggle={handleReceiptToggle}
                        onReceiptUpload={handleReceiptUpload}
                        onReceiptDelete={setReceiptToDelete}
                      />
                    ))}
                    {transactions.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>No transactions</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Submit */}
          {!isReadOnly && transactions.length > 0 && (
            <div className={styles.submitArea}>
              {submitError && <div className={styles.submitError}>{submitError}</div>}
              {submitSuccess && <div className={styles.submitSuccess}>{submitSuccess}</div>}
              <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting || unreviewed > 0}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              {unreviewed > 0 && <p className={styles.submitHint}>{unreviewed} transaction(s) still need commentary</p>}
            </div>
          )}
        </>
      )}

      {/* Delete receipt confirmation */}
      <ConfirmDialog
        open={!!receiptToDelete}
        title="Delete Receipt"
        message={receiptToDelete ? `Delete "${receiptToDelete.fileName}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={handleReceiptDelete}
        onCancel={() => setReceiptToDelete(null)}
      />
    </div>
  );
}

// ─── Transaction Row with Receipt Support ───────────────────────────────────

function TransactionRow({ tx, readOnly, saving, receiptCount, isReceiptExpanded, receiptList, loadingReceipts, uploadingReceipt, onBlur, onFlagToggle, onReceiptToggle, onReceiptUpload, onReceiptDelete }: {
  tx: CardTransactionListItem; readOnly: boolean; saving: boolean;
  receiptCount: number; isReceiptExpanded: boolean; receiptList: CardReceipt[];
  loadingReceipts: boolean; uploadingReceipt: boolean;
  onBlur: (id: string, commentary: string, personal: boolean, disputed: boolean) => void;
  onFlagToggle: (id: string, field: 'personal' | 'disputed', value: boolean) => void;
  onReceiptToggle: (txId: string) => void;
  onReceiptUpload: (txId: string, file: File) => void;
  onReceiptDelete: (receipt: CardReceipt) => void;
}) {
  const [commentary, setCommentary] = useState(tx.commentary || '');
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setCommentary(tx.commentary || ''); }, [tx.commentary]);
  const hasCommentary = commentary.trim() !== '';

  return (
    <>
      <tr className={`${styles.txRow} ${!hasCommentary && !readOnly ? styles.txRowUnreviewed : ''}`}>
        <td className={styles.txDate}>{new Date(tx.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
        <td className={styles.txMerchant}>{tx.merchantName}</td>
        <td className={styles.txCategory}>{tx.merchantCategory || '—'}</td>
        <td className={styles.txAmount} style={{ color: tx.amount < 0 ? 'var(--green)' : undefined }}>
          {tx.amount < 0 ? '-' : ''}{tx.currency} {Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </td>
        <td>
          {readOnly ? (
            <span className={styles.txCommentaryReadOnly}>{tx.commentary || <span style={{ color: 'var(--gray-400)' }}>—</span>}</span>
          ) : (
            <input type="text" className={`${styles.txCommentaryInput} ${saving ? styles.txSaving : ''}`} value={commentary}
              onChange={e => setCommentary(e.target.value)}
              onBlur={() => { if (commentary !== (tx.commentary || '')) onBlur(tx.cardTransactionId, commentary, tx.isFlaggedPersonal, tx.isFlaggedDisputed); }}
              placeholder="Add commentary..." />
          )}
        </td>
        <td style={{ textAlign: 'center' }}>
          <input type="checkbox" checked={tx.isFlaggedPersonal} disabled={readOnly} onChange={e => onFlagToggle(tx.cardTransactionId, 'personal', e.target.checked)} />
        </td>
        <td style={{ textAlign: 'center' }}>
          <input type="checkbox" checked={tx.isFlaggedDisputed} disabled={readOnly} onChange={e => onFlagToggle(tx.cardTransactionId, 'disputed', e.target.checked)} />
        </td>
        <td style={{ textAlign: 'center' }}>
          <button
            className={styles.receiptBtn}
            onClick={() => onReceiptToggle(tx.cardTransactionId)}
            title={receiptCount > 0 ? `${receiptCount} receipt(s)` : 'Attach receipt'}
            style={{
              background: isReceiptExpanded ? 'var(--turquoise-light)' : receiptCount > 0 ? 'var(--green-light)' : 'var(--gray-100)',
              color: isReceiptExpanded ? 'var(--turquoise-dark)' : receiptCount > 0 ? 'var(--green)' : 'var(--gray-500)',
              border: isReceiptExpanded ? '1px solid var(--turquoise)' : '1px solid transparent',
            }}
          >
            📎 {receiptCount > 0 && <span style={{ fontSize: 10, fontWeight: 700 }}>{receiptCount}</span>}
          </button>
        </td>
      </tr>

      {/* Expanded receipt panel */}
      {isReceiptExpanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0, borderTop: 'none' }}>
            <div style={{ padding: '12px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 1 }}>Receipts</span>
                {!readOnly && (
                  <>
                    <input ref={fileRef} type="file" accept="image/*,.pdf" hidden onChange={e => { if (e.target.files?.[0]) onReceiptUpload(tx.cardTransactionId, e.target.files[0]); e.target.value = ''; }} />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploadingReceipt}
                      style={{ fontSize: 12, fontWeight: 600, color: 'var(--turquoise-dark)', background: 'var(--white)', border: '1px solid var(--turquoise)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                    >
                      {uploadingReceipt ? 'Uploading...' : '+ Upload'}
                    </button>
                  </>
                )}
              </div>

              {loadingReceipts ? (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>Loading...</div>
              ) : receiptList.length === 0 ? (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>No receipts attached.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {receiptList.map(r => (
                    <div key={r.cardTransactionReceiptId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--white)', borderRadius: 6, border: '1px solid var(--gray-200)' }}>
                      <span style={{ fontSize: 16 }}>{r.contentType.startsWith('image/') ? '🖼️' : '📄'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a
                          href="#"
                          onClick={async (e) => { e.preventDefault(); try { const token = await api.getToken(); const resp = await fetch(VITE_API_BASE_URL + `/api/card-transaction-receipts/${r.cardTransactionReceiptId}/download`, { headers: { Authorization: 'Bearer ' + token } }); const blob = await resp.blob(); const url = URL.createObjectURL(blob); window.open(url); } catch { /* ignore */ } }}
                          style={{ fontSize: 13, fontWeight: 600, color: 'var(--turquoise-dark)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        >
                          {r.fileName}
                        </a>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                          {(r.fileSize / 1024).toFixed(0)} KB · {r.uploadedByName} · {new Date(r.uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </div>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => onReceiptDelete(r)}
                          style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}
                          title="Delete receipt"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
